import React, { useEffect, useContext } from "react"
import useLoading from "../../hooks/useLoading"
import { AuthContext, OnSuccessfulAuthCallback } from "./context"
import { CommonFunctionCtx } from "../../context/commonFunctionContext"
import { isLocalStorageAvailable } from "../../utils/isLocalStorageAvailable"
import { useLocation } from "react-router-dom"
import { UserCtx } from "../../context/userContext"
import { UserInfo } from "../../api/app.service"
import { useNavigate } from "react-router-dom"
import { userflowLogout } from "../../utils/userflow-integration"
import { useToast } from "../../hooks/useToast"

export function useAuthModal() {
  const { user, clearUser } = useContext(UserCtx)
  const { endActiveSession } = useContext(CommonFunctionCtx)
  const { toast } = useToast()
  const { startLoading, stopLoading } = useLoading()
  const location = useLocation()
  const navigate = useNavigate()

  const {
    setOpen,
    setAuthAction,
    setEmail,
    setAccountType,
    setOnSuccessfulAuth,
    setPhone,
    setFirstName,
    setLastName,
  } = useContext(AuthContext)

  const showLogin = (options?: {
    email?: string
    onSuccess?: OnSuccessfulAuthCallback
  }) => {
    if (options?.email) {
      setEmail(options.email)
    } else {
      setEmail("")
    }

    if (options?.onSuccess) {
      setOnSuccessfulAuth(() => options.onSuccess)
    } else {
      setOnSuccessfulAuth(undefined)
    }
    setAuthAction("login")
    navigate(`/${user?.activeProfile}`)
    setOpen(false)
  }

  interface CoachRegisterOptions {
    accountType: "coach"
    email?: string
    onSuccess?: OnSuccessfulAuthCallback
    phone?: string
    firstName?: string
    lastName?: string
  }

  interface RegisterOptions {
    accountType?: "member" | null
    email?: string
    onSuccess?: OnSuccessfulAuthCallback
  }

  useEffect(() => {
    let currentLocation = `${location.pathname}${location.search}`
    const storedLocation = localStorage.getItem("currentLocation")

    currentLocation = currentLocation.replace(/\/$/, "")

    if (currentLocation.includes("received_invites=true")) {
      localStorage.setItem("currentLocation", currentLocation)
    } else {
      if (storedLocation && storedLocation !== currentLocation) {
        setOpen(false)
      }

      if (currentLocation === "/sign-in" && user?.activeProfile) {
        localStorage.setItem("currentLocation", `/${user.activeProfile}`)
      }
      localStorage.setItem("currentLocation", currentLocation)
    }
  }, [location, user])

  const showRegister = (options?: CoachRegisterOptions | RegisterOptions) => {
    if (options?.email) {
      setEmail(options.email)
    } else {
      setEmail("")
    }

    if (options?.accountType) {
      setAccountType(options.accountType)
    } else {
      setAccountType(null)
    }

    if (options?.onSuccess) {
      setOnSuccessfulAuth(() => options.onSuccess)
    } else {
      setOnSuccessfulAuth(undefined)
    }

    if (options?.accountType === "coach") {
      if (options?.phone) {
        setPhone(options.phone)
      } else {
        setPhone("")
      }

      if (options?.firstName) {
        setFirstName(options.firstName)
      } else {
        setFirstName("")
      }

      if (options?.lastName) {
        setLastName(options.lastName)
      } else {
        setLastName("")
      }
    }

    setAuthAction("register")
    setOpen(true)
  }

  const logout = async () => {
    if (user) {
      startLoading()
      await UserInfo.updateNextStepsValues({
        user_id: parseInt(user.user_id),
        active_profile_id: user.activeProfileId,
        created_chat: false,
        profile_access_status: "",
      })
        .then(() => {
          endActiveSession()
        })
        .then(() => {
          if (isLocalStorageAvailable()) {
            localStorage.setItem("logout", Date.now().toString())

            const showNewFeatureValue = localStorage.getItem("showNewFeature")

            localStorage.clear()

            if (showNewFeatureValue !== null) {
              localStorage.setItem("showNewFeature", showNewFeatureValue)
            }
          }
          clearUser()
          window.location.replace("/")
        })
        .then(() => {
          // Clear 3rd party userflow identity on logout
          userflowLogout();
        })
        .catch((ex) => {
          console.error(ex)
          toast.error(ex.response.data.message)
          stopLoading()
        })
    }
  }

  return { showLogin, showRegister, logout }
}
